import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Flag, ShieldCheck, AlertOctagon, MessageSquare, Paperclip, X, Loader2 } from "lucide-react";

// CSRF + cookies are added by the shared `api` axios instance — see
// /app/frontend/src/lib/api.js. Never use raw axios here, or
// state-changing requests get rejected with "Jeton CSRF manquant".

const TRUST_META = {
  trusted:   { label: "DE CONFIANCE", color: "text-emerald-300 border-emerald-500/60 bg-emerald-500/10" },
  normal:    { label: "NORMAL",       color: "text-zinc-300 border-zinc-700 bg-zinc-800/50" },
  watchlist: { label: "À SURVEILLER", color: "text-amber-300 border-amber-500/60 bg-amber-500/10" },
  high_risk: { label: "RISQUE ÉLEVÉ", color: "text-rose-300 border-rose-500/60 bg-rose-500/10" },
};

const PRIORITY_META = {
  normal:    { label: "Normal",    cls: "border-zinc-700 text-zinc-300" },
  important: { label: "Important", cls: "border-amber-500/60 text-amber-300" },
  urgent:    { label: "Urgent",    cls: "border-rose-500/60 text-rose-300" },
};

/**
 * UserProfilePanel — collapsible per-user dossier surfaced on the Admin
 * Users tab. Shows:
 *   • Advisory risk score + trust label (greyed when low_confidence).
 *   • Manual flag toggle with required reason — written to audit log.
 *   • "Send direct message" form with optional image/PDF attachment.
 *   • Recent thread history.
 *
 * REMINDER: nothing rendered here affects alert delivery or ordering.
 * Risk score & trust label are advisory triage information only.
 */
export const UserProfilePanel = ({ phone, onChange }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        api.get(`/admin/users/${encodeURIComponent(phone)}/profile`),
        api.get(`/admin/users/${encodeURIComponent(phone)}/messages`),
      ]);
      setProfile(p.data);
      setMessages(m.data.items || []);
    } catch (_e) {
      toast.error("Impossible de charger le profil");
    } finally { setLoading(false); }
  }, [phone]);

  useEffect(() => {
    let mounted = true;
    const run = async () => { if (mounted) await refresh(); };
    run();
    return () => { mounted = false; };
  }, [refresh]);

  if (loading || !profile) {
    return (
      <div className="mt-2 border-t border-zinc-800 pt-2 text-[10px] text-zinc-500 font-mono flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Profil…
      </div>
    );
  }

  const s = profile.stats || {};
  const lowConf = !!s.low_confidence;
  const tlMeta = TRUST_META[s.trust_label] || TRUST_META.normal;

  return (
    <div className="mt-3 border-t border-zinc-800 pt-3 space-y-3" data-testid={`admin-user-profile-${phone}`}>
      {/* Risk + trust label */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 border ${lowConf ? "text-zinc-500 border-zinc-700 bg-zinc-900/60" : tlMeta.color}`}
              data-testid={`admin-user-trust-label-${phone}`}>
          {lowConf ? "DONNÉES INSUFF." : tlMeta.label}
        </span>
        <span className={`text-[10px] font-mono ${lowConf ? "text-zinc-600" : "text-zinc-300"} tabular-nums`}
              title="Score 0–100 — plus haut = plus susceptible d'abuser"
              data-testid={`admin-user-risk-score-${phone}`}>
          score · {s.risk_score ?? "—"}/100 {lowConf && <span className="text-zinc-700">(min {5})</span>}
        </span>
        {s.manual_flag && (
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 border border-rose-500/70 text-rose-300 bg-rose-500/10"
                title={s.manual_flag_reason || ""}
                data-testid={`admin-user-manual-flag-${phone}`}>
            <Flag className="w-3 h-3 inline mr-0.5" /> Signalé
          </span>
        )}
      </div>

      <div className="grid grid-cols-4 gap-1.5 text-center font-mono">
        <Stat n={s.total_alerts} l="total" tone="zinc" />
        <Stat n={s.legitimate_alerts} l="légit." tone="emerald" />
        <Stat n={s.suspect_alerts} l="suspect" tone="orange" />
        <Stat n={s.false_alerts} l="fausses" tone="rose" />
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={() => { setSendOpen(v => !v); setFlagOpen(false); }}
          data-testid={`admin-user-send-toggle-${phone}`}
          className="border border-emerald-500/50 text-emerald-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-500 hover:text-black flex items-center justify-center gap-1.5"
        >
          <Send className="w-3 h-3" /> Envoyer message
        </button>
        <button
          type="button"
          onClick={() => { setFlagOpen(v => !v); setSendOpen(false); }}
          data-testid={`admin-user-flag-toggle-${phone}`}
          className={`border py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-1.5 ${
            s.manual_flag
              ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500 hover:text-black"
              : "border-rose-500/50 text-rose-300 hover:bg-rose-500 hover:text-black"
          }`}
        >
          {s.manual_flag ? <ShieldCheck className="w-3 h-3" /> : <Flag className="w-3 h-3" />}
          {s.manual_flag ? "Retirer signalement" : "Signaler"}
        </button>
      </div>

      {sendOpen && (
        <SendMessageForm
          phone={phone}
          onSent={async () => { setSendOpen(false); await refresh(); onChange?.(); }}
          onCancel={() => setSendOpen(false)}
        />
      )}

      {flagOpen && (
        <ManualFlagForm
          phone={phone}
          currentlyFlagged={!!s.manual_flag}
          onDone={async () => { setFlagOpen(false); await refresh(); onChange?.(); }}
          onCancel={() => setFlagOpen(false)}
        />
      )}

      {messages.length > 0 && (
        <div className="border border-zinc-800 bg-zinc-950/50 p-2 space-y-1.5" data-testid={`admin-user-thread-${phone}`}>
          <div className="text-[9px] font-bold tracking-[0.25em] uppercase text-zinc-500 mb-1">Historique</div>
          {messages.slice(0, 4).map((m) => {
            const pm = PRIORITY_META[m.priority] || PRIORITY_META.normal;
            return (
              <div key={m.id} className="text-[10px] border-l border-zinc-700 pl-2 py-0.5">
                <div className="flex items-center justify-between gap-2">
                  <span className={`px-1 border ${pm.cls} text-[9px] uppercase tracking-[0.15em]`}>{pm.label}</span>
                  <span className="font-mono text-zinc-500">{new Date(m.created_at).toLocaleString("fr-FR", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</span>
                </div>
                <div className="text-zinc-300 font-bold mt-0.5">{m.title}</div>
                <div className="text-zinc-400 line-clamp-2">{m.body}</div>
                <div className="flex items-center gap-1.5 mt-0.5 text-zinc-500">
                  {m.attachment && <Paperclip className="w-3 h-3" />}
                  <span className="font-mono">{m.read ? "lu" : "non lu"}</span>
                  {m.sender_admin_name && <span className="text-zinc-600">· par {m.sender_admin_name}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Stat = ({ n, l, tone }) => {
  const color = { zinc:"text-zinc-200", emerald:"text-emerald-300", orange:"text-orange-300", rose:"text-rose-300" }[tone] || "text-zinc-200";
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 px-1 py-1">
      <div className={`text-base font-black tabular-nums ${color}`}>{n ?? 0}</div>
      <div className="text-[8px] uppercase tracking-[0.15em] text-zinc-500">{l}</div>
    </div>
  );
};

const SendMessageForm = ({ phone, onSent, onCancel }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return toast.error("Titre et corps sont obligatoires");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("body", body.trim());
      fd.append("priority", priority);
      if (file) fd.append("file", file);
      await api.post(`/admin/users/${encodeURIComponent(phone)}/messages`, fd);
      toast.success("Message envoyé");
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border border-emerald-500/40 bg-zinc-900/60 p-2 space-y-1.5" data-testid={`admin-user-send-form-${phone}`}>
      <div className="text-[9px] font-bold tracking-[0.2em] uppercase text-emerald-400 flex items-center gap-1">
        <MessageSquare className="w-3 h-3" /> Nouveau message · {phone}
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Titre"
        maxLength={120}
        data-testid={`admin-user-send-title-${phone}`}
        className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-[11px] px-2 py-1.5"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Message au citoyen"
        rows={3}
        data-testid={`admin-user-send-body-${phone}`}
        className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-[11px] px-2 py-1.5 resize-none"
      />
      <div className="grid grid-cols-2 gap-1.5">
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          data-testid={`admin-user-send-priority-${phone}`}
          className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-[11px] px-2 py-1.5"
        >
          <option value="normal">Normal</option>
          <option value="important">Important</option>
          <option value="urgent">Urgent</option>
        </select>
        <label className="border border-dashed border-zinc-700 text-zinc-300 text-[10px] px-2 py-1.5 flex items-center justify-center gap-1.5 cursor-pointer hover:border-emerald-500/60 hover:text-emerald-300">
          <Paperclip className="w-3 h-3" />
          {file ? file.name.slice(0, 24) : "Pièce jointe (image/PDF)"}
          <input
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            data-testid={`admin-user-send-file-${phone}`}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      <div className="flex items-center justify-end gap-1.5 pt-1">
        <button
          type="button"
          onClick={onCancel}
          data-testid={`admin-user-send-cancel-${phone}`}
          className="px-2 py-1 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase tracking-[0.15em] hover:text-zinc-100"
        >
          <X className="w-3 h-3 inline" /> Annuler
        </button>
        <button
          type="submit"
          disabled={busy}
          data-testid={`admin-user-send-submit-${phone}`}
          className="px-2.5 py-1 border border-emerald-500/60 text-emerald-300 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-500 hover:text-black disabled:opacity-50"
        >
          {busy ? "…" : "Envoyer"}
        </button>
      </div>
    </form>
  );
};

const ManualFlagForm = ({ phone, currentlyFlagged, onDone, onCancel }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const flagging = !currentlyFlagged;
    if (flagging && !reason.trim()) return toast.error("Une raison est obligatoire");
    setBusy(true);
    try {
      await api.post(`/admin/users/${encodeURIComponent(phone)}/flag`, {
        flagged: flagging,
        reason: flagging ? reason.trim() : "",
      });
      toast.success(flagging ? "Citoyen signalé (audit log)" : "Signalement retiré");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };

  if (currentlyFlagged) {
    return (
      <div className="border border-emerald-500/40 bg-zinc-900/60 p-2 space-y-1.5" data-testid={`admin-user-flag-form-${phone}`}>
        <div className="text-[10px] text-zinc-300">Retirer le signalement manuel sur ce citoyen?</div>
        <div className="flex items-center justify-end gap-1.5">
          <button type="button" onClick={onCancel} className="px-2 py-1 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase">Annuler</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid={`admin-user-flag-unflag-${phone}`}
            className="px-2.5 py-1 border border-emerald-500/60 text-emerald-300 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-500 hover:text-black disabled:opacity-50"
          >
            {busy ? "…" : "Retirer"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-rose-500/40 bg-zinc-900/60 p-2 space-y-1.5" data-testid={`admin-user-flag-form-${phone}`}>
      <div className="text-[9px] font-bold tracking-[0.2em] uppercase text-rose-400 flex items-center gap-1">
        <AlertOctagon className="w-3 h-3" /> Signalement manuel · audit
      </div>
      <div className="text-[10px] text-zinc-400">
        Le signalement n&apos;empêche AUCUNE alerte d&apos;être délivrée. Il sert
        uniquement de note de contexte pour les opérateurs. Une raison est obligatoire.
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Raison (obligatoire)"
        rows={2}
        data-testid={`admin-user-flag-reason-${phone}`}
        className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-[11px] px-2 py-1.5 resize-none"
      />
      <div className="flex items-center justify-end gap-1.5">
        <button type="button" onClick={onCancel} className="px-2 py-1 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase">Annuler</button>
        <button
          type="submit"
          disabled={busy || !reason.trim()}
          data-testid={`admin-user-flag-submit-${phone}`}
          className="px-2.5 py-1 border border-rose-500/60 text-rose-300 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-rose-500 hover:text-black disabled:opacity-50"
        >
          {busy ? "…" : "Signaler"}
        </button>
      </div>
    </form>
  );
};

export default UserProfilePanel;
